import * as React from 'react';
import { Button } from '@/components/ui/button';
import { QrKod } from '@/components/ui/qr';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';
import { Alan } from '@/components/ui/field';
import { ApiHatasi, api } from '@/lib/api';

/**
 * Misafir mobil ayarlari + oda QR'lari.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Misafir Mobil.dc.html".
 *
 * Bu ekran olmadan ozellik KULLANILAMAZDI: `misafir_mobil_acik` varsayilan
 * olarak kapali gelir. Ozelligi acmak icin gereken TEK sey o kutudur;
 * Wi-Fi ve WhatsApp istege baglidir (tasarimdaki kartlar) ve bos
 * birakilirsa misafir ekraninda hic gosterilmezler.
 *
 * QR'lar tarayicida uretilir (qrcode-generator, bagimliliksiz). "Yazdir"
 * dogrudan tarayicinin baski onizlemesini acar; ayri bir PDF hizmetine
 * ihtiyac yok ve otel bir kez basip odalara asar.
 */

interface Ayar {
  ad: string;
  slug: string;
  misafirMobilAcik: boolean;
  wifiAd: string | null;
  wifiSifre: string | null;
  misafirWhatsapp: string | null;
  misafirKok: string;
}

interface Oda {
  id: string;
  no: string;
  kat: string | null;
}

export function MisafirMobilAyar() {
  const [ayar, setAyar] = React.useState<Ayar | null>(null);
  const [odalar, setOdalar] = React.useState<Oda[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [kaydediliyor, setKaydediliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);
  const [kaydedildi, setKaydedildi] = React.useState(false);

  const [acik, setAcik] = React.useState(false);
  const [wifiAd, setWifiAd] = React.useState('');
  const [wifiSifre, setWifiSifre] = React.useState('');
  const [whatsapp, setWhatsapp] = React.useState('');

  const yukle = React.useCallback(async () => {
    try {
      const [a, o] = await Promise.all([
        api<Ayar>('/api/tesis-ayarlari'),
        api<{ odalar: Oda[] }>('/api/tesis-ayarlari/odalar'),
      ]);
      setAyar(a);
      setOdalar(o.odalar);
      setAcik(a.misafirMobilAcik);
      setWifiAd(a.wifiAd ?? '');
      setWifiSifre(a.wifiSifre ?? '');
      setWhatsapp(a.misafirWhatsapp ?? '');
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Ayarlar alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    setKaydediliyor(true);
    setHata(null);
    try {
      await api('/api/tesis-ayarlari', {
        method: 'PATCH',
        body: JSON.stringify({
          misafirMobilAcik: acik,
          wifiAd: wifiAd.trim() || null,
          wifiSifre: wifiSifre.trim() || null,
          misafirWhatsapp: whatsapp.trim() || null,
        }),
      });
      setKaydedildi(true);
      setTimeout(() => setKaydedildi(false), 2000);
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kaydedilemedi.');
    } finally {
      setKaydediliyor(false);
    }
  }

  if (yukleniyor) {
    return (
      <main className="flex flex-1 items-center justify-center bg-zemin p-7 text-gri">
        <span className="text-sm">Yükleniyor…</span>
      </main>
    );
  }

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both] print:hidden">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Misafir mobil</h1>
        <Rozet renk={acik ? 'basari' : 'notr'}>{acik ? 'Açık' : 'Kapalı'}</Rozet>
      </div>

      {hata && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800 print:hidden">{hata}</div>
      )}

      <div className="grid items-start gap-[18px] print:block xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <form onSubmit={kaydet} className="flex flex-col gap-[18px] print:hidden">
          <Kart>
            <KartBaslik>
              <h2 className="text-[17px] font-bold tracking-[-.01em]">Odadaki QR</h2>
            </KartBaslik>
            <KartGovde className="flex flex-col gap-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={acik}
                  onChange={(e) => setAcik(e.target.checked)}
                  className="mt-1 size-4"
                />
                <span className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-lacivert">Misafir mobili aç</span>
                  <span className="text-[13px] leading-relaxed text-gri">
                    Özelliği açmak için gereken tek şey budur. Kapalıyken{' '}
                    <code className="rounded bg-satir px-1">/oda/…</code> adresleri hiç açılmaz; QR'ları
                    basmadan önce açın. Aşağıdaki alanlar isteğe bağlıdır.
                  </span>
                </span>
              </label>

              <Alan
                etiket="Wi-Fi ağ adı"
                value={wifiAd}
                onChange={(e) => setWifiAd(e.target.value)}
                maxLength={64}
                ipucu="Boş bırakılırsa misafir ekranında Wi-Fi kartı gösterilmez."
              />
              <Alan
                etiket="Wi-Fi parolası"
                value={wifiSifre}
                onChange={(e) => setWifiSifre(e.target.value)}
                maxLength={64}
                autoComplete="off"
                ipucu="Odada zaten basılı olduğu için şifrelenmeden saklanır."
              />
              <Alan
                etiket="WhatsApp numarası"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                maxLength={30}
                inputMode="tel"
                ipucu={'Misafir ekranındaki "Resepsiyona yaz" kartı. Boşsa kart gösterilmez.'}
              />

              {/*
                Bu kutu once "oda numarasi tahmin edilebilir" uyarisiydi.
                Kimlik kapisi eklenince o acik kapandi; eski metni birakmak
                tesise olmayan bir riski anlatirdi.
              */}
              <div className="rounded-xl border border-cizgi bg-zemin px-4 py-3 text-[13px] leading-relaxed text-gri">
                <strong className="text-lacivert">QR tek başına yetmez.</strong> Misafir QR'ı okutunca önce
                kimlik numarası sorulur; numara o odada <em>şu an</em> konaklayan birine ait değilse menü
                açılmaz. Odada üç kişi kalıyorsa üçünden herhangi biri girebilir. Check-out olunca bağlantı
                kendiliğinden kapanır.
              </div>

              <div className="flex items-center gap-3">
                <Button type="submit" yukleniyor={kaydediliyor}>
                  Kaydet
                </Button>
                {kaydedildi && <span className="text-sm font-semibold text-yesil">Kaydedildi</span>}
              </div>
            </KartGovde>
          </Kart>
        </form>

        <Kart className="print:border-0 print:shadow-none">
          <KartBaslik className="print:hidden">
            <h2 className="text-[17px] font-bold tracking-[-.01em]">Oda QR'ları</h2>
            <div className="ml-auto flex items-center gap-3">
              <span className="text-[13px] text-gri">{odalar.length} oda</span>
              <Button varyant="ikincil" boyut="sm" onClick={() => window.print()} disabled={!odalar.length}>
                Yazdır
              </Button>
            </div>
          </KartBaslik>
          <KartGovde className="print:p-0">
            {!acik && (
              <p className="mb-4 rounded-xl bg-satir px-4 py-3 text-[13px] text-gri print:hidden">
                Misafir mobil kapalı: bu QR'lar okutulduğunda sayfa açılmaz. Önce yukarıdan açın.
              </p>
            )}
            {odalar.length === 0 ? (
              <p className="text-[13px] text-gri">Tanımlı oda yok.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 print:grid-cols-3 print:gap-4">
                {odalar.map((oda) => {
                  const adres = `${ayar?.misafirKok}/${oda.no}`;
                  return (
                    <div
                      key={oda.id}
                      className="flex break-inside-avoid flex-col items-center gap-2 rounded-2xl border border-cizgi bg-white p-4 text-center"
                    >
                      <span className="text-[22px] font-bold leading-none tracking-[-.02em]">{oda.no}</span>
                      <QrKod deger={adres} />
                      <span className="text-[11px] font-semibold leading-tight text-gri">{ayar?.ad}</span>
                      <span className="break-all text-[9px] leading-tight text-[#8a93ad]">
                        {adres.replace('https://', '')}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </KartGovde>
        </Kart>
      </div>
    </main>
  );
}
